import { formatUtcInTimezone } from '../../lib/time.js';

function RoleBadge({ role }) {
  if (role === 'admin') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-accent-soft px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-accent">
        <i className="fa-solid fa-user-shield text-[10px]" aria-hidden="true" /> Admin
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-line bg-canvas px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-mist">
      <i className="fa-solid fa-user text-[10px]" aria-hidden="true" /> User
    </span>
  );
}

function StatusBadge({ active }) {
  return active ? (
    <span className="inline-flex items-center gap-1 rounded-full bg-success-soft px-2 py-0.5 text-[11px] font-semibold text-success">
      <span className="h-1.5 w-1.5 rounded-full bg-success" aria-hidden="true" /> Active
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 rounded-full border border-line bg-canvas px-2 py-0.5 text-[11px] font-semibold text-mist">
      <span className="h-1.5 w-1.5 rounded-full bg-mist" aria-hidden="true" /> Inactive
    </span>
  );
}

export function UserTable({ users, currentUserId, viewerTimezone, onEdit, onResetPassword, onDelete }) {
  if (users.length === 0) {
    return (
      <div className="card flex flex-col items-center justify-center px-6 py-16 text-center">
        <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-accent-soft text-accent">
          <i className="fa-solid fa-users text-lg" aria-hidden="true" />
        </div>
        <p className="text-sm font-medium text-ink">No users yet</p>
        <p className="mt-1 text-sm text-mist">Use the "Create user" button to register the first operator.</p>
      </div>
    );
  }

  return (
    <div className="card overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="border-b border-line bg-canvas/60 text-left text-[11px] font-semibold uppercase tracking-wider text-mist">
              <th className="px-5 py-3">User</th>
              <th className="px-5 py-3">Role</th>
              <th className="px-5 py-3">Status</th>
              <th className="px-5 py-3">Timezone</th>
              <th className="px-5 py-3">Last login</th>
              <th className="px-5 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => {
              const isSelf = u.id === currentUserId;
              return (
                <tr key={u.id} className="border-b border-line last:border-b-0 hover:bg-canvas/40">
                  <td className="px-5 py-3 align-middle">
                    <div className="flex items-center gap-3">
                      <div className="flex h-9 w-9 items-center justify-center rounded-full bg-accent-soft text-sm font-semibold text-accent">
                        {(u.displayName || u.login).slice(0, 1).toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 truncate font-semibold text-ink">
                          <span className="truncate">{u.displayName || u.login}</span>
                          {isSelf && (
                            <span className="rounded bg-accent-soft px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-accent">
                              You
                            </span>
                          )}
                        </div>
                        <div className="truncate text-[12px] text-mist">@{u.login}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-3 align-middle"><RoleBadge role={u.role} /></td>
                  <td className="px-5 py-3 align-middle"><StatusBadge active={u.isActive} /></td>
                  <td className="px-5 py-3 align-middle">
                    <span className="text-ink">{u.timezone}</span>
                  </td>
                  <td className="px-5 py-3 align-middle text-mist">
                    {u.lastLoginAt ? formatUtcInTimezone(u.lastLoginAt, viewerTimezone) : '—'}
                  </td>
                  <td className="px-5 py-3 align-middle">
                    <div className="flex justify-end gap-1">
                      <button
                        type="button"
                        onClick={() => onEdit(u)}
                        className="inline-flex h-9 w-9 items-center justify-center rounded-xl text-mist transition-colors hover:bg-canvas hover:text-ink"
                        title="Edit"
                        aria-label={`Edit ${u.login}`}
                      >
                        <i className="fa-solid fa-pen" aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        onClick={() => onResetPassword(u)}
                        className="inline-flex h-9 w-9 items-center justify-center rounded-xl text-mist transition-colors hover:bg-canvas hover:text-ink"
                        title="Reset password"
                        aria-label={`Reset password for ${u.login}`}
                      >
                        <i className="fa-solid fa-key" aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        onClick={() => onDelete(u)}
                        disabled={isSelf}
                        className="inline-flex h-9 w-9 items-center justify-center rounded-xl text-mist transition-colors hover:bg-rose-soft hover:text-rose-600 disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-mist"
                        title={isSelf ? 'Cannot delete your own account' : 'Delete'}
                        aria-label={`Delete ${u.login}`}
                      >
                        <i className="fa-solid fa-trash" aria-hidden="true" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
